import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, DollarSign, Users, TrendingUp } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

type MonthRow = {
  month: string;
  active_referrals: number;
  gross_cents: number;
  commission_cents: number;
};

function fmtUsd(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayoutsTab({ orgId }: { orgId: string }) {
  const accrualQ = useQuery({
    queryKey: ["partner-accrual", orgId],
    queryFn: async (): Promise<MonthRow[]> => {
      const { data, error } = await supabase.rpc("partner_commission_accrual" as any, {
        _affiliate_id: orgId,
      });
      if (error) throw error;
      return (data ?? []) as MonthRow[];
    },
  });

  const totals = useMemo(() => {
    const rows = accrualQ.data ?? [];
    const last = rows[rows.length - 1];
    const ytd = rows.reduce((s, r) => s + Number(r.commission_cents), 0);
    return {
      currentMonth: last ? Number(last.commission_cents) : 0,
      activeReferrals: last ? Number(last.active_referrals) : 0,
      ytd,
    };
  }, [accrualQ.data]);

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-muted/40 text-sm text-muted-foreground">
        Emerging Market is the merchant-of-record on Paddle, so referred subscriptions settle to us. Commissions
        below accrue at 20% of the referred plan price for 12 months. Payouts are issued manually via ACH each
        month once you cross the $50 minimum — reach out to the team for banking details.
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Metric icon={<DollarSign className="h-5 w-5" />} label="This month accrual" value={fmtUsd(totals.currentMonth)} />
        <Metric icon={<Users className="h-5 w-5" />} label="Active referrals" value={String(totals.activeReferrals)} />
        <Metric icon={<TrendingUp className="h-5 w-5" />} label="Last 12 months" value={fmtUsd(totals.ytd)} />
      </div>

      <Card className="p-6">
        <h3 className="font-display text-lg mb-4">Monthly accrual</h3>
        {accrualQ.isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (accrualQ.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No referred subscriptions yet. Share your referral link from the dashboard to start attributing signups.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2">Month</th>
                  <th className="text-right py-2">Active referrals</th>
                  <th className="text-right py-2">Gross</th>
                  <th className="text-right py-2">Commission</th>
                </tr>
              </thead>
              <tbody>
                {(accrualQ.data ?? []).map((r) => (
                  <tr key={r.month} className="border-b border-border/50">
                    <td className="py-2">{new Date(r.month).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</td>
                    <td className="text-right">{Number(r.active_referrals)}</td>
                    <td className="text-right">{fmtUsd(Number(r.gross_cents))}</td>
                    <td className="text-right font-medium">
                      {fmtUsd(Number(r.commission_cents))}
                      {Number(r.commission_cents) >= 5000 && (
                        <Badge className="ml-2 text-[10px] bg-primary/15 text-primary border-primary/30">payout eligible</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </Card>
  );
}